import { useEffect, useState } from "react";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";

import { COLORS } from "../../../../../theme/colors";
import { Icon } from "../../../../../components/base";
import endPoints from "../../../../../services/apiEndPoints";
import { DOWNLOADED_EXCEL_FILE_TYPE } from "../../../../../constants";
import { getData, putData } from "../../../../../services";
import fill from "../../../../../assets/images/icons/workspace/fill.svg";
import { formatDatetime, nFormatter } from "../../../../../services/utils";
import uploadImage from "../../../../../assets/images/icons/workspace/uploadIcon.svg";
import downloadImage from "../../../../../assets/images/icons/workspace/downloadIcon.svg";
import grayRightArrow from "../../../../../assets/images/icons/workspace/grayRightArrow.svg";
import activeUploadImage from "../../../../../assets/images/icons/workspace/activeUploadIcon.svg";
import { ReactComponent as RightArrow } from "../../../../../assets/images/icons/rightArrow.svg";

const useStyles = makeStyles(() => ({
  heading: {
    fontSize: 30,
    fontWeight: 700,
    marginBottom: 30,
  },
  iconBox: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 66,
    paddingBottom: 200,
  },
  fillText: {
    fontSize: 18,
    fontWeight: 400,
    marginTop: 20,
    textAlign: "center",
    color: COLORS.gray[3],
  },
  downloadText: {
    fontSize: 18,
    fontWeight: 600,
    marginTop: 12,
    color: COLORS.purple,
  },
  uploadText: {
    fontSize: 18,
    fontWeight: 600,
    marginTop: 12,
    textAlign: "center",
    color: COLORS.gray[3],
  },
  img: {
    marginLeft: 60,
  },
  svgIcon: {
    marginTop: 10,
    width: 38,
    height: 60,
    color: COLORS.black,
    "& path": {
      strokeWidth: 1,
    },
  },
}));

const DOWNLOADED_FILE_NAME = "units_list.xlsx";

const UnitData = ({ building, onError, setUnitsFileUploaded }) => {
  const classes = useStyles();

  const [download, setDownload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [unitsFile, setUnitsFile] = useState();

  const unitsXlsxUrl = endPoints.OB_OPERATOR_UNITS_XLSX.replace("{id}", building.id);
  const fileDownloaded = unitsFile?.downloaded_at;

  useEffect(() => {
    getDownloadedFileInfo();
  }, []);

  const getDownloadedFileInfo = () => {
    getData(unitsXlsxUrl, (data) => {
      setUnitsFile(data);
      if (data.file) {
        setUnitsFileUploaded(true);
      }
    });
  };

  const uploadFile = (e) => {
    if (!fileDownloaded || uploading) {
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    putData(
      unitsXlsxUrl,
      () => {
        getDownloadedFileInfo();
      },
      formData,
      { "Content-Type": "multipart/form-data" }
    )
      .catch(onError)
      .finally(() => {
        setUploading(false);
      });
  };

  const downloadFile = () => {
    setDownload(true);
    getData(
      endPoints.OB_OPERATOR_DOWNLOAD_XLSX.replace("{id}", building.id),
      (data) => {
        const file = new Blob([data], { type: DOWNLOADED_EXCEL_FILE_TYPE });
        const element = document.createElement("a");
        element.href = URL.createObjectURL(file);
        element.download = DOWNLOADED_FILE_NAME;
        document.body.appendChild(element);
        element.click();
        getDownloadedFileInfo();
      },
      null,
      {
        responseType: "blob",
      }
    )
      .catch(onError)
      .finally(() => setDownload(false));
  };

  return (
    <Box>
      <Typography className={classes.heading}>
        Don&apos;t crack under pressure...We need also the units data
      </Typography>
      <Typography>
        Please download unit data template below, fill in the details and ulpoad when done.
        <br />
        We&apos;ll send you a reminder X days after dowloading
      </Typography>

      <Box display="flex" justifyContent="space-between" mt={10} pb={20} alignItems="flex-start">
        <Box>
          <Icon src={downloadImage} hoverable="true" onClick={downloadFile} root={classes.img} />
          <Typography className={classes.downloadText}>Download units template</Typography>
          <Box mt={2}>
            {download && <Typography>Downloading...</Typography>}
            {fileDownloaded && (
              <>
                <span>
                  <b>{DOWNLOADED_FILE_NAME}</b> {nFormatter(unitsFile.empty_file_size)}B
                </span>
                <Typography style={{ color: COLORS.darks[1] }}>
                  Downloaded on {formatDatetime(fileDownloaded, "MMM DD, YYYY HH:mm")}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <RightArrow className={classes.svgIcon} />

        <Box style={{ marginTop: 10, opacity: !fileDownloaded ? 0.5 : 1 }}>
          <Icon src={fill} hoverable="true" root={classes.img} />
          <Typography className={classes.fillText}>Fill in the required fields </Typography>
        </Box>

        {fileDownloaded ? (
          <RightArrow className={classes.svgIcon} />
        ) : (
          <Icon src={grayRightArrow} />
        )}

        <Box style={{ opacity: !fileDownloaded ? 0.5 : 1 }}>
          <label>
            <Grid container direction="column">
              <Icon src={fileDownloaded ? activeUploadImage : uploadImage} hoverable="true" />
              <Typography
                className={classes.uploadText}
                style={{ color: fileDownloaded ? COLORS.purple : undefined }}
              >
                Upload the filled file
              </Typography>
              <input
                accept=".xls, .xlsx"
                id="icon-button-file"
                type="file"
                style={{ display: "none" }}
                onChange={uploadFile}
              />
            </Grid>
          </label>
          <Box mt={2}>
            {uploading && <Typography>Uploading...</Typography>}
            {unitsFile?.file && (
              <>
                <span>
                  <b>{unitsFile.file.split("/").pop()}</b>{" "}
                  {nFormatter(unitsFile?.uploaded_file_size)}B
                </span>
                <Typography style={{ color: COLORS.darks[1] }}>
                  Uploaded on {formatDatetime(unitsFile?.uploaded_at, "MMM DD, YYYY HH:mm")}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UnitData;
